import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import UserAvatar from 'components/shared/user/UserAvatar';
import { roles } from 'common/constants/options';
import StyledWeeklySessionEventContainer from 'common/theme/calendar/event/week/StyledWeeklySessionEventContainer';
import { getSessionLineColor, isPastSession, dimIfPastSx } from 'common/utils/sessionColor';
import { isClient } from 'common/utils/user';

const fmtTime = (d) => {
  try { return format(new Date(d), 'HH:mm'); } catch { return ''; }
};

/**
 * Tarjeta de sesión en la vista semanal. Ocupa toda la franja de su hora y muestra:
 * indicador de estado + avatar/nombre (entrenador para el cliente, cliente para admin/entrenador)
 * + categoría + rango horario. Al hacer click se abre el modal con el detalle.
 */
function WeeklySessionEvent({ event, viewType = roles.administrator }) {
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { extendedProps: eventProps } = event;
  const { status } = eventProps;
  const category = eventProps.session_type;
  const user = isClient(viewType) ? eventProps.trainer : eventProps.client;
  const timeRange = event.start ? `${fmtTime(event.start)}${event.end ? ` – ${fmtTime(event.end)}` : ''}` : '';
  // Misma semántica de color que las cards del cliente: franja IZQUIERDA por estado + atenuar pasadas.
  const lineColor = getSessionLineColor(theme, status, event.start);
  const past = isPastSession(event.start, event.end);

  return (
    <StyledWeeklySessionEventContainer sx={{ borderLeft: `4px solid ${lineColor}`, ...dimIfPastSx(past) }}>
      <Box sx={{ flex: 1, minWidth: 0, p: { xs: 0.75, sm: 1 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <UserAvatar size={isExtraSmall ? 18 : 24} textSize={isExtraSmall ? 9 : 12} user={user} />
          <Typography noWrap sx={{ fontWeight: 700, fontSize: isExtraSmall ? '0.7rem' : '0.78rem', color: '#FFFFFF' }}>
            {user?.full_name}
          </Typography>
        </Stack>
        {category && (
          <Typography noWrap sx={{ fontSize: isExtraSmall ? '0.62rem' : '0.7rem', color: '#F9BD4E', fontWeight: 600, lineHeight: 1.2 }}>
            {category}
          </Typography>
        )}
        {timeRange && (
          <Typography noWrap sx={{ fontSize: isExtraSmall ? '0.6rem' : '0.68rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.2 }}>
            {timeRange}
          </Typography>
        )}
      </Box>
    </StyledWeeklySessionEventContainer>
  );
}

WeeklySessionEvent.propTypes = {
  event: PropTypes.object.isRequired,
  viewType: PropTypes.string
};

export default WeeklySessionEvent;
